import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProtectedRoute from '../../components/base/ProtectedRoute';
import type { DataItem } from '../admin/components/UploadSection';
import DataCard from './components/DataCard';
import SearchBar from './components/SearchBar';
import TasksList from './components/TasksList';
import NotificationPanel from './components/NotificationPanel';
import { getNotificationsForClient } from '../../services/notifications';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'Pending' | 'In Progress' | 'Done' | 'Blocked';
  dueDate: string;
  createdAt: string;
  projectNumber: string;
  clientId: string;
}

export default function PortalPage() {
  const { logout, user, getClients } = useAuth();
  const navigate = useNavigate();
  const [dataItems, setDataItems] = useState<DataItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<DataItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [clientId, setClientId] = useState<string>('');

  useEffect(() => {
    if (user) {
      // Resolve the client's actual id from the accounts list
      const clients = getClients();
      const clientAccount = clients.find(c => c.username === user.username);
      const resolvedClientId = clientAccount?.id;

      if (!resolvedClientId) return;

      setClientId(resolvedClientId);

      // Load data items — filtered by clientId (the real id, not username)
      const stored = localStorage.getItem('admin_data_items');
      if (stored) {
        const allItems: DataItem[] = JSON.parse(stored);
        const clientItems = allItems.filter(
          item => item.clientId === resolvedClientId && !item.isPlaceholder
        );
        setDataItems(clientItems);
        setFilteredItems(clientItems);
      }

      // Load tasks — stored as Record<"clientId_projectNumber", Task[]>
      const storedTasks = localStorage.getItem('admin_project_tasks');
      if (storedTasks) {
        try {
          const allTasksMap: Record<string, Task[]> = JSON.parse(storedTasks);
          const clientPrefix = `${resolvedClientId}_`;
          const clientTasks: Task[] = [];
          Object.entries(allTasksMap).forEach(([key, taskList]) => {
            if (key.startsWith(clientPrefix)) {
              const projectNumber = key.slice(clientPrefix.length);
              taskList.forEach(task => {
                clientTasks.push({ ...task, projectNumber, clientId: resolvedClientId });
              });
            }
          });
          setTasks(clientTasks);
        } catch {
          setTasks([]);
        }
      }

      // Load unread notification count
      updateUnreadCount(resolvedClientId);
    }
  }, [user]);

  useEffect(() => {
    // Update unread count when panel closes
    if (!isNotificationPanelOpen && clientId) {
      updateUnreadCount(clientId);
    }
  }, [isNotificationPanelOpen, clientId]);

  const updateUnreadCount = (id: string) => {
    const notifications = getNotificationsForClient(id);
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems(dataItems);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = dataItems.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.fileName?.toLowerCase().includes(query) ||
        item.projectNumber?.toLowerCase().includes(query) ||
        item.projectName?.toLowerCase().includes(query)
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, dataItems]);

  // Group items by project number, reading projectStatus from items
  const groupedByProject = filteredItems.reduce((acc, item) => {
    const projectKey = item.projectNumber || 'unassigned';
    if (!acc[projectKey]) {
      acc[projectKey] = {
        projectNumber: item.projectNumber || 'Unassigned',
        projectName: item.projectName || 'No Project Name',
        status: (item as any).projectStatus || 'Active',
        items: []
      };
    }
    acc[projectKey].items.push(item);
    return acc;
  }, {} as Record<string, { projectNumber: string; projectName: string; status: string; items: DataItem[] }>);

  const projectGroups = Object.values(groupedByProject);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleProjectTasks = (projectNumber: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectNumber)) {
        newSet.delete(projectNumber);
      } else {
        newSet.add(projectNumber);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: string }> = {
      'Active': { bg: 'bg-green-50', text: 'text-green-700', icon: 'ri-play-circle-line' },
      'In Process': { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'ri-loader-4-line' },
      'Completed': { bg: 'bg-gray-100', text: 'text-gray-700', icon: 'ri-checkbox-circle-line' },
      'On Hold': { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-pause-circle-line' }
    };
    const config = statusConfig[status] || statusConfig['Active'];
    return (
      <div className={`flex items-center gap-2 px-3 py-1 ${config.bg} ${config.text} text-sm font-medium rounded-full`}>
        <i className={`${config.icon} text-base`}></i>
        <span>{status}</span>
      </div>
    );
  };

  const getProgressBar = (projectNumber: string) => {
    const projectTasks = getProjectTasks(projectNumber);
    if (projectTasks.length === 0) return null;
    const doneTasks = projectTasks.filter(t => t.status === 'Done').length;
    const total = projectTasks.length;
    const percentage = Math.round((doneTasks / total) * 100);

    let barColor = 'bg-teal-500';
    if (percentage === 100) barColor = 'bg-green-500';
    else if (percentage >= 50) barColor = 'bg-teal-500';
    else if (percentage > 0) barColor = 'bg-amber-400';
    else barColor = 'bg-gray-300';

    return (
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
            <i className="ri-bar-chart-line text-sm"></i>
            Task Progress
          </span>
          <span className="text-xs font-semibold text-gray-700">
            {doneTasks} / {total} Done
            <span className="ml-1 text-gray-400">({percentage}%)</span>
          </span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const getProjectTasks = (projectNumber: string) => {
    return tasks.filter(t => t.projectNumber === projectNumber);
  };

  const handleViewProject = (projectNumber: string) => {
    navigate(`/portal/project/${projectNumber}`);
  };

  // Summary stats
  const totalProjects = projectGroups.length;
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'Done').length;
  const overallCompletion = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  let completionBarColor = 'bg-gray-300';
  if (overallCompletion === 100) completionBarColor = 'bg-green-500';
  else if (overallCompletion >= 50) completionBarColor = 'bg-teal-500';
  else if (overallCompletion > 0) completionBarColor = 'bg-amber-400';

  return (
    <ProtectedRoute requiredRole="client">
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Client Portal</h1>
                <p className="text-sm text-gray-600 mt-1">Welcome, {user?.username}</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Notification Bell */}
                <button
                  onClick={() => setIsNotificationPanelOpen(true)}
                  className="relative w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-notification-3-line text-xl text-gray-700"></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-logout-box-line text-base"></i>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />

            {/* Summary Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Total Projects */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl shrink-0">
                  <i className="ri-folder-3-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Projects</p>
                  <p className="text-3xl font-bold text-gray-900 leading-tight">{totalProjects}</p>
                </div>
              </div>

              {/* Total Tasks */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-indigo-50 rounded-xl shrink-0">
                  <i className="ri-task-line text-2xl text-indigo-500"></i>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Tasks</p>
                  <p className="text-3xl font-bold text-gray-900 leading-tight">{totalTasks}</p>
                </div>
              </div>

              {/* Tasks Done */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tasks Done</p>
                  <p className="text-3xl font-bold text-gray-900 leading-tight">{doneTasks}</p>
                </div>
              </div>

              {/* Overall Completion */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                      <i className="ri-pie-chart-line text-lg text-amber-500"></i>
                    </div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Completion Rate</p>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{overallCompletion}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${completionBarColor}`}
                    style={{ width: `${overallCompletion}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-400 mt-2">{doneTasks} of {totalTasks} tasks completed</p>
              </div>
            </div>

            {/* Documents count */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2">
                <i className="ri-file-list-3-line text-xl text-teal-600"></i>
                <span className="text-sm text-gray-600">
                  {filteredItems.length} {filteredItems.length === 1 ? 'document' : 'documents'} available
                  {searchQuery && ` (filtered from ${dataItems.length})`}
                </span>
              </div>
            </div>

            {/* Projects & Documents */}
            {filteredItems.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <i className="ri-inbox-line text-5xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchQuery ? 'No results found' : 'No documents available'}
                </h3>
                <p className="text-sm text-gray-600">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : "Your administrator hasn't uploaded any documents yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {projectGroups.map((group, index) => {
                  const projectTasks = getProjectTasks(group.projectNumber);
                  const isExpanded = expandedProjects.has(group.projectNumber);

                  return (
                    <div key={index} className="space-y-4">
                      {/* Project Header */}
                      <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                              <i className="ri-folder-line text-xl text-teal-600"></i>
                            </div>
                            <div>
                              <h2 className="text-lg font-bold text-gray-900">{group.projectNumber}</h2>
                              <p className="text-sm text-gray-600">{group.projectName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getStatusBadge(group.status)}
                            <div className="flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 text-sm font-medium rounded-full">
                              <i className="ri-file-line text-base"></i>
                              <span>{group.items.length} {group.items.length === 1 ? 'item' : 'items'}</span>
                            </div>
                            <button
                              onClick={() => handleViewProject(group.projectNumber)}
                              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <span>View Project</span>
                              <i className="ri-arrow-right-line text-base"></i>
                            </button>
                          </div>
                        </div>
                        {getProgressBar(group.projectNumber)}
                      </div>

                      {/* Tasks Section */}
                      {projectTasks.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <button
                            onClick={() => toggleProjectTasks(group.projectNumber)}
                            className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                                <i className="ri-task-line text-xl text-blue-600"></i>
                              </div>
                              <div className="text-left">
                                <h3 className="text-base font-semibold text-gray-900">Project Tasks</h3>
                                <p className="text-sm text-gray-600">{projectTasks.length} {projectTasks.length === 1 ? 'task' : 'tasks'}</p>
                              </div>
                            </div>
                            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xl text-gray-400`}></i>
                          </button>

                          {isExpanded && (
                            <div className="border-t border-gray-200 p-5">
                              <TasksList tasks={projectTasks} />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Documents Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {group.items.map(item => (
                          <DataCard key={item.id} item={item} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        {/* Notification Panel */}
        <NotificationPanel
          clientId={clientId}
          isOpen={isNotificationPanelOpen}
          onClose={() => setIsNotificationPanelOpen(false)}
        />
      </div>
    </ProtectedRoute>
  );
}
